import { IsIn, IsOptional, IsString, IsUUID, MaxLength, MinLength } from 'class-validator';

/** Mirrors `SERVICE_REQUEST_*` on the entity — contracts stay free of database imports. */
export const SERVICE_REQUEST_KINDS = [
  'enforcement',
  'information',
  'records',
  'file_access',
] as const;
export type ServiceRequestKind = (typeof SERVICE_REQUEST_KINDS)[number];

export const SERVICE_REQUEST_STATUSES = [
  'submitted',
  'accepted',
  'in_progress',
  'settled',
  'declined',
] as const;
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

export type ServiceRequestDirection = 'lg_to_company' | 'company_to_lg';

/** Where each status may go next. Settled and declined are answers, so final. */
export const SERVICE_REQUEST_NEXT: Record<ServiceRequestStatus, readonly ServiceRequestStatus[]> = {
  submitted: ['accepted', 'declined'],
  accepted: ['in_progress', 'settled', 'declined'],
  in_progress: ['settled', 'declined'],
  settled: [],
  declined: [],
};

export interface ServiceRequestSummary {
  id: string;
  localGovernmentId: string;
  localGovernmentName: string;
  companyId: string;
  companyName: string;
  direction: ServiceRequestDirection;
  kind: ServiceRequestKind;
  subject: string;
  address: string;
  description: string;
  status: ServiceRequestStatus;
  settlementSummary: string | null;
  linkedFileId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

/** Asking the other side. The route says which side the caller is on. */
export class CreateServiceRequestDto {
  /** The other party: the company when a municipality asks, the municipality when a company does. */
  @IsUUID()
  counterpartId!: string;

  @IsIn(SERVICE_REQUEST_KINDS)
  kind!: ServiceRequestKind;

  @IsString()
  @MinLength(3)
  @MaxLength(200)
  subject!: string;

  @IsOptional() @IsString() @MaxLength(400) address?: string;
  @IsOptional() @IsString() @MaxLength(5000) description?: string;
}

/**
 * The handling side moving a request on. A summary is required to settle or
 * decline — it is the answer the asking side gets.
 */
export class TransitionServiceRequestDto {
  @IsIn(SERVICE_REQUEST_STATUSES)
  to!: ServiceRequestStatus;

  @IsOptional() @IsString() @MaxLength(5000) summary?: string;
  @IsOptional() @IsUUID() linkedFileId?: string;
}
